/*
  One Tooth World — product page extras (phase 07):
    - [data-otw-recs]   recommendations carousel: fetches /recommendations/products and swaps the cards in
    - <otw-recent>      recently viewed: localStorage `otw_recent` (max 12, deduped), cards via otw-product-card-render
    - [data-otw-clamp]  6-line description clamp with Read more / Read less
*/
const OTW = (window.OTW = window.OTW || {});
const RECENT_KEY = 'otw_recent';
const RECENT_MAX = 12;
const designMode = () => Boolean(window.Shopify && window.Shopify.designMode);

/* ---------- Recommendations ---------- */
async function loadRecs(section) {
  if (section.dataset.loaded) return;
  section.dataset.loaded = 'true';
  const content = section.querySelector('[data-otw-recs-content]');
  try {
    const response = await fetch(section.dataset.url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
    if (!response.ok) throw new Error(String(response.status));
    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    const fresh = doc.querySelector('[data-otw-recs-content]');
    if (fresh && fresh.querySelector('[data-otw-card]')) {
      content.innerHTML = fresh.innerHTML;
      section.hidden = false;
    } else if (!designMode()) {
      section.hidden = true;
    }
  } catch (error) {
    if (!designMode()) section.hidden = true;
  }
}

function observeRecs(section) {
  if (!('IntersectionObserver' in window)) {
    loadRecs(section);
    return;
  }
  const observer = new IntersectionObserver(
    (entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        observer.disconnect();
        loadRecs(section);
      }
    },
    { rootMargin: '400px 0px' }
  );
  observer.observe(section);
}

/* ---------- Recently viewed ---------- */
function readRecent() {
  try {
    const list = JSON.parse(window.localStorage.getItem(RECENT_KEY) || '[]');
    return Array.isArray(list) ? list.filter((h) => typeof h === 'string') : [];
  } catch (error) {
    return [];
  }
}

function writeRecent(list) {
  try {
    window.localStorage.setItem(RECENT_KEY, JSON.stringify(list));
  } catch (error) {
    /* storage unavailable: nothing to remember */
  }
}

class OtwRecent extends HTMLElement {
  async connectedCallback() {
    if (this.started) return;
    this.started = true;
    const current = this.dataset.handle;
    const limit = Number(this.dataset.limit) || RECENT_MAX;
    const root = this.dataset.root || '/';

    let list = readRecent();
    if (current) {
      list = [current, ...list.filter((h) => h !== current)].slice(0, RECENT_MAX);
      writeRecent(list);
    }
    const handles = list.filter((h) => h !== current).slice(0, limit);
    if (!handles.length) return;

    const track = this.querySelector('[data-otw-recent-track]');
    const template = this.querySelector('template[data-otw-recent-skeleton]');
    const skeletons = [];
    if (template) {
      handles.slice(0, 5).forEach(() => {
        const node = template.content.firstElementChild.cloneNode(true);
        skeletons.push(node);
        track.appendChild(node);
      });
    }
    this.hidden = false;

    const results = await Promise.all(
      handles.map((handle) =>
        OTW.fetchSection(`${root}products/${handle}`, 'otw-product-card-render')
          .then((el) => (el && el.querySelector('[data-otw-card]') ? el.innerHTML : null))
          .catch(() => null)
      )
    );
    skeletons.forEach((node) => node.remove());
    let rendered = 0;
    results.forEach((html) => {
      if (!html) return;
      const slide = document.createElement('div');
      slide.className = 'otw-slider__slide';
      slide.innerHTML = html;
      track.appendChild(slide);
      rendered += 1;
    });
    if (!rendered && !designMode()) this.hidden = true;
  }
}

if (!customElements.get('otw-recent')) customElements.define('otw-recent', OtwRecent);

/* ---------- Description clamp ---------- */
function initClamp(root) {
  if (root.dataset.ready) return;
  root.dataset.ready = 'true';
  const body = root.querySelector('.otw-pd-clamp__body');
  const toggle = root.querySelector('[data-otw-clamp-toggle]');
  if (!body || !toggle) return;

  const measure = () => {
    if (root.classList.contains('is-open')) return;
    toggle.hidden = !(body.scrollHeight > body.clientHeight + 2);
  };
  toggle.addEventListener('click', () => {
    const open = !root.classList.contains('is-open');
    root.classList.toggle('is-open', open);
    toggle.setAttribute('aria-expanded', String(open));
    toggle.textContent = open ? root.dataset.less : root.dataset.more;
  });
  measure();
  window.addEventListener('load', measure);
  if ('ResizeObserver' in window) new ResizeObserver(measure).observe(body);
}

const boot = () => {
  document.querySelectorAll('[data-otw-recs]').forEach(observeRecs);
  document.querySelectorAll('[data-otw-clamp]').forEach(initClamp);
};
boot();
document.addEventListener('shopify:section:load', boot);
